/** EoRFailureOverlay — End of Round 失敗時のコンティニュー誘導モーダル(Phase A2 Day 8)。
 *  Reference: docs/MONETIZATION.md §4.2 (冷酷な簡潔さ)、docs/CLAUDE_DESIGN_BRIEF.md §4.5
 *  - "Almost Complete!" + 占領率進捗バー(あと X% 表示)
 *  - "Continue with 900 Color" メイン CTA(Phase A はモック、SDK なし)
 *  - "Exit Stage" 控えめ
 *  - 装飾的な「Game Over」アニメは入れない(MONETIZATION §4.2 禁則事項)
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import type { GridConfig } from '../core/types';

export interface EoRFailureOverlayOptions {
  readonly grid: GridConfig;
  readonly onContinue: () => void;
  readonly onExit: () => void;
  /** 動的な Color 残高(設定すれば show() 毎に balance を反映) */
  readonly getColorBalance?: () => number;
}

const COLOR = {
  backdrop: 0x2c2419,
  modalBg: 0xf8f1e4,
  frameGold: 0xc8a85c,
  inkBlack: 0x2c2419,
  inkWarmGrey: 0x6b5d4f,
  museFlora: 0x4a7c3f,
  dangerCrimson: 0xb5483a,
  buttonText: 0xf8f1e4,
} as const;

const CONTINUE_PRICE = 900;
const MOCK_COLOR_BALANCE = 800; // Day 8: 残高赤字表示の検証用にあえて 900 未満

export class EoRFailureOverlay {
  public readonly view: Container;

  private readonly progressText: Text;
  private readonly missingText: Text;
  private readonly progressBar: Graphics;
  private readonly barLayout: { x: number; y: number; width: number; height: number };
  private readonly balanceText: Text;
  private readonly getColorBalance?: () => number;

  constructor(options: EoRFailureOverlayOptions) {
    const { grid, onContinue, onExit } = options;
    this.getColorBalance = options.getColorBalance;

    this.view = new Container();
    this.view.visible = false;
    this.view.eventMode = 'static';

    const backdrop = new Graphics();
    backdrop.rect(0, 0, grid.canvasPx, grid.canvasPx);
    backdrop.fill({ color: COLOR.backdrop, alpha: 0.62 });
    this.view.addChild(backdrop);

    const modalWidth = grid.canvasPx * 0.84;
    const modalHeight = grid.canvasPx * 0.7;
    const modalX = (grid.canvasPx - modalWidth) / 2;
    const modalY = (grid.canvasPx - modalHeight) / 2;

    const modalBg = new Graphics();
    modalBg.roundRect(modalX, modalY, modalWidth, modalHeight, 16);
    modalBg.fill({ color: COLOR.modalBg });
    modalBg.stroke({ width: 2, color: COLOR.frameGold });
    this.view.addChild(modalBg);

    const corners = new Graphics();
    const cornerOffset = 14;
    [
      { x: modalX + cornerOffset, y: modalY + cornerOffset },
      { x: modalX + modalWidth - cornerOffset, y: modalY + cornerOffset },
      { x: modalX + cornerOffset, y: modalY + modalHeight - cornerOffset },
      { x: modalX + modalWidth - cornerOffset, y: modalY + modalHeight - cornerOffset },
    ].forEach((c) => corners.circle(c.x, c.y, 3));
    corners.fill({ color: COLOR.frameGold });
    this.view.addChild(corners);

    const titleStyle = new TextStyle({
      fontFamily: 'Cormorant Garamond, Georgia, serif',
      fontSize: 36,
      fill: COLOR.inkBlack,
      fontWeight: '600',
    });
    const title = new Text({ text: 'Almost Complete!', style: titleStyle });
    title.anchor.set(0.5, 0);
    title.x = grid.canvasPx / 2;
    title.y = modalY + 28;
    this.view.addChild(title);

    const progressStyle = new TextStyle({
      fontFamily: 'Inter, "Helvetica Neue", sans-serif',
      fontSize: 16,
      fill: COLOR.inkWarmGrey,
    });
    this.progressText = new Text({ text: '', style: progressStyle });
    this.progressText.anchor.set(0.5, 0);
    this.progressText.x = grid.canvasPx / 2;
    this.progressText.y = modalY + 84;
    this.view.addChild(this.progressText);

    const barWidth = modalWidth * 0.7;
    const barHeight = 12;
    const barX = (grid.canvasPx - barWidth) / 2;
    const barY = modalY + 124;
    this.barLayout = { x: barX, y: barY, width: barWidth, height: barHeight };

    const barBg = new Graphics();
    barBg.roundRect(barX, barY, barWidth, barHeight, 6);
    barBg.fill({ color: COLOR.inkWarmGrey, alpha: 0.18 });
    barBg.stroke({ width: 1, color: COLOR.frameGold, alpha: 0.4 });
    this.view.addChild(barBg);

    this.progressBar = new Graphics();
    this.view.addChild(this.progressBar);

    const missingStyle = new TextStyle({
      fontFamily: 'Inter, "Helvetica Neue", sans-serif',
      fontSize: 14,
      fill: COLOR.dangerCrimson,
    });
    this.missingText = new Text({ text: '', style: missingStyle });
    this.missingText.anchor.set(0.5, 0);
    this.missingText.x = grid.canvasPx / 2;
    this.missingText.y = barY + barHeight + 16;
    this.view.addChild(this.missingText);

    const continueW = modalWidth * 0.7;
    const continueH = 56;
    const continueX = (grid.canvasPx - continueW) / 2;
    const continueY = modalY + modalHeight - 156;

    const continueBtn = new Graphics();
    continueBtn.roundRect(continueX, continueY, continueW, continueH, 12);
    continueBtn.fill({ color: COLOR.museFlora });
    continueBtn.stroke({ width: 2, color: COLOR.frameGold, alpha: 0.45 });
    continueBtn.eventMode = 'static';
    continueBtn.cursor = 'pointer';
    continueBtn.on('pointertap', () => {
      console.info(`[Canvas Kingdom] Continue purchased (mock: -${CONTINUE_PRICE} Color)`);
      this.hide();
      onContinue();
    });
    this.view.addChild(continueBtn);

    const continueLabelStyle = new TextStyle({
      fontFamily: 'Inter, "Helvetica Neue", sans-serif',
      fontSize: 18,
      fill: COLOR.buttonText,
      fontWeight: '600',
    });
    const continueLabel = new Text({
      text: `Continue with ${CONTINUE_PRICE} Color`,
      style: continueLabelStyle,
    });
    continueLabel.anchor.set(0.5, 0.5);
    continueLabel.x = continueX + continueW / 2;
    continueLabel.y = continueY + continueH / 2;
    continueLabel.eventMode = 'none';
    this.view.addChild(continueLabel);

    const balanceStyle = new TextStyle({
      fontFamily: 'Inter, "Helvetica Neue", sans-serif',
      fontSize: 12,
      fill: COLOR.inkWarmGrey,
    });
    this.balanceText = new Text({
      text: `Color: ${MOCK_COLOR_BALANCE}`,
      style: balanceStyle,
    });
    this.balanceText.anchor.set(0.5, 0);
    this.balanceText.x = grid.canvasPx / 2;
    this.balanceText.y = continueY + continueH + 12;
    this.view.addChild(this.balanceText);

    const exitStyle = new TextStyle({
      fontFamily: 'Inter, "Helvetica Neue", sans-serif',
      fontSize: 14,
      fill: COLOR.inkWarmGrey,
    });
    const exitBtn = new Text({ text: 'Exit Stage', style: exitStyle });
    exitBtn.anchor.set(0.5, 0);
    exitBtn.x = grid.canvasPx / 2;
    exitBtn.y = modalY + modalHeight - 36;
    exitBtn.eventMode = 'static';
    exitBtn.cursor = 'pointer';
    exitBtn.on('pointertap', () => {
      this.hide();
      onExit();
    });
    this.view.addChild(exitBtn);
  }

  show(currentCoverage: number, targetCoverage: number, reason: string): void {
    const pct = (currentCoverage * 100).toFixed(1);
    const targetPct = (targetCoverage * 100).toFixed(0);
    const missingPct = Math.max(0, (targetCoverage - currentCoverage) * 100).toFixed(1);

    this.progressText.text = `Coverage ${pct}% / ${targetPct}%`;
    this.missingText.text = `あと ${missingPct}% で完成 — ${reason}`;

    this.progressBar.clear();
    const ratio = Math.min(1, targetCoverage > 0 ? currentCoverage / targetCoverage : 0);
    const filledWidth = this.barLayout.width * ratio;
    if (filledWidth > 0) {
      this.progressBar.roundRect(
        this.barLayout.x,
        this.barLayout.y,
        filledWidth,
        this.barLayout.height,
        6,
      );
      this.progressBar.fill({ color: COLOR.museFlora });
    }

    // Color 残高を動的更新(callback があれば優先)
    const balance = this.getColorBalance ? this.getColorBalance() : MOCK_COLOR_BALANCE;
    this.balanceText.text = `Color: ${balance.toLocaleString('en-US')}`;
    this.balanceText.style.fill = balance < CONTINUE_PRICE ? COLOR.dangerCrimson : COLOR.inkWarmGrey;

    this.view.visible = true;
  }

  hide(): void {
    this.view.visible = false;
  }

  isVisible(): boolean {
    return this.view.visible;
  }
}
